import { addDays, normalizeInvoice, toIsoDate } from "./calculations";
import type { BillingProfile, Invoice, LineItem } from "./types";
import { emptyParty } from "./types";

export function createId(): string {
  return crypto.randomUUID();
}

export function createLineItem(): LineItem {
  return {
    id: createId(),
    description: "",
    quantity: 1,
    unit: "",
    rateMinor: 0,
    amountMinor: 0,
  };
}

export function createBlankInvoice(options: {
  ownerUserId: string;
  profileId: string;
  profileName: string;
  invoiceNumber: string;
  paymentDays: number;
  issuer?: Partial<Invoice["issuer"]>;
  paymentInstructions?: string;
}): Invoice {
  const now = new Date();
  const iso = now.toISOString();
  const issueDate = toIsoDate(now);
  return normalizeInvoice({
    id: createId(),
    ownerUserId: options.ownerUserId,
    billingProfileId: options.profileId,
    number: options.invoiceNumber,
    status: "draft",
    issueDate,
    dueDate: addDays(issueDate, options.paymentDays),
    purchaseOrderNumber: "",
    projectReference: "",
    currency: "HKD",
    language: "en",
    paperSize: "a4",
    issuer: {
      ...emptyParty(),
      name: options.profileName,
      ...options.issuer,
    },
    client: emptyParty(),
    items: [createLineItem()],
    discount: null,
    subtotalMinor: 0,
    discountMinor: 0,
    totalMinor: 0,
    clientNotes: "",
    internalNotes: "",
    paymentInstructions: options.paymentInstructions ?? "",
    terms: "",
    authorisedName: "",
    payments: [],
    receipts: [],
    paidMinor: 0,
    balanceMinor: 0,
    createdAt: iso,
    updatedAt: iso,
  });
}

export function createProfile(
  ownerUserId: string,
  displayName: string,
): BillingProfile {
  const now = new Date().toISOString();
  const prefix =
    displayName
      .normalize("NFKD")
      .replace(/[^\p{L}\p{N}]+/gu, " ")
      .trim()
      .split(/\s+/)
      .slice(0, 3)
      .map((word) => word[0]?.toUpperCase() ?? "")
      .join("") || "INV";

  return {
    id: createId(),
    ownerUserId,
    displayName,
    logoPath: "",
    address: "",
    email: "",
    phone: "",
    website: "",
    registrationNumber: "",
    invoicePrefix: prefix,
    defaultPaymentDays: 30,
    brandColour: "",
    paymentInstructions: "",
    archivedAt: null,
    createdAt: now,
    updatedAt: now,
  };
}

export function profileIssuer(profile: BillingProfile): Invoice["issuer"] {
  return {
    name: profile.displayName,
    contactName: "",
    email: profile.email,
    phone: profile.phone,
    address: profile.address,
    registrationNumber: profile.registrationNumber,
  };
}
